import axiosClient from './axiosClient';

/**
 * Typed axios wrappers for `/api/admin/globals/questionnaires`.
 *
 * Parent versioning mirrors the other globals (tasks, adventures, cohorts):
 * draft `current_version` → publish → tenants sync. Questionnaires are
 * structurally richer — each parent owns an ordered list of
 * `questionnaire_group`s, each of which owns an ordered list of
 * `question`s. Groups/questions mutate in place (no per-sub-entity version
 * column) and are returned inline on the GET detail envelope.
 *
 * Keep this file in lockstep with the route handlers — the 409
 * `{error:'version_conflict', current_version}` shape on parent PATCH and
 * the nested sub-entity mutation routes under
 * `/questionnaires/:code/groups` + `/groups/:group_id/questions`.
 */

/* ------------------------------------------------------------------ */
/*  Sub-entity shapes                                                  */
/* ------------------------------------------------------------------ */

/**
 * Single question choice — used by SINGLE_CHOICE / MULTI_CHOICE questions.
 * Backend stores these on the question as a JSONB array; the dialog
 * surfaces them as editable rows with min-2 enforced client-side.
 */
export interface QuestionChoice {
  choice_code: string;
  choice_label: string;
}

export type QuestionType =
  | 'TEXT'
  | 'LONG_TEXT'
  | 'NUMBER'
  | 'SINGLE_CHOICE'
  | 'MULTI_CHOICE'
  | 'BOOLEAN'
  | 'DATE';

export interface QuestionRow {
  question_id: number;
  question_code: string;
  question_text: string;
  question_type: QuestionType;
  required: boolean;
  display_order: number;
  choices: QuestionChoice[] | null;
}

export interface QuestionGroupRow {
  group_id: number;
  group_code: string;
  group_name: string;
  display_order: number;
  questions: QuestionRow[];
}

/* ------------------------------------------------------------------ */
/*  Parent shapes                                                      */
/* ------------------------------------------------------------------ */

export interface QuestionnaireHistoryRow {
  version: number;
  questionnaire_name: string | null;
  language_code: string;
  platform_questionnaire_id: string | number | null;
  active: boolean;
  change_summary: string | null;
  change_type: 'create' | 'update' | 'delete' | 'publish' | 'rollback';
  created_at: string;
  created_by: string | null;
}

export interface AdminQuestionnaireRow {
  questionnaire_code: string;
  questionnaire_name: string | null;
  language_code: string;
  platform_questionnaire_id: string | number | null;
  active: boolean;
  current_version: number;
  updated_at: string;
  updated_by: string | null;
  status: 'in_sync' | 'never_published' | 'unpublished';
}

export interface AdminQuestionnaireDetail extends AdminQuestionnaireRow {
  groups: QuestionGroupRow[];
  history: QuestionnaireHistoryRow[];
}

export interface ListQuestionnairesResponse {
  rows: AdminQuestionnaireRow[];
}

export interface ListQuestionnairesParams {
  sort?: 'questionnaire_code' | 'questionnaire_name' | 'updated_at' | 'current_version';
  dir?: 'asc' | 'desc';
  status?: 'in_sync' | 'never_published';
}

export interface CreateQuestionnairePayload {
  questionnaire_code: string;
  questionnaire_name: string;
  language_code: string;
  change_summary?: string;
}

export interface UpdateQuestionnairePayload {
  base_version: number;
  questionnaire_name?: string;
  language_code?: string;
  active?: boolean;
  change_summary?: string;
}

/* ------------------------------------------------------------------ */
/*  Sub-entity payloads                                                */
/* ------------------------------------------------------------------ */

export interface CreateGroupPayload {
  group_code: string;
  group_name: string;
  display_order: number;
}

export interface UpdateGroupPayload {
  group_name?: string;
  display_order?: number;
}

export interface CreateQuestionPayload {
  question_code: string;
  question_text: string;
  question_type: QuestionType;
  required: boolean;
  display_order: number;
  choices?: QuestionChoice[] | null;
}

export interface UpdateQuestionPayload {
  question_text?: string;
  question_type?: QuestionType;
  required?: boolean;
  display_order?: number;
  choices?: QuestionChoice[] | null;
}

/* ------------------------------------------------------------------ */
/*  Publish + Rollback                                                  */
/* ------------------------------------------------------------------ */

export interface PublishQuestionnaireEntityRequest {
  questionnaire_code: string;
  base_version: number;
}

export interface PublishQuestionnairesRequest {
  entities: PublishQuestionnaireEntityRequest[];
  batch_id?: string;
  change_summary?: string;
}

export interface PublishQuestionnaireEntityResult {
  questionnaire_code: string;
  ok: boolean;
  published_version?: number;
  error?: string;
  message?: string;
  current_version?: number;
}

export interface PublishQuestionnairesResponse {
  batch_id: string;
  results: PublishQuestionnaireEntityResult[];
}

export interface RollbackQuestionnairePayload {
  target_version: number;
  base_version: number;
  change_summary?: string;
}

const base = '/api/admin/globals/questionnaires';

export const adminQuestionnaireApi = {
  list: async (
    params: ListQuestionnairesParams = {},
  ): Promise<AdminQuestionnaireRow[]> => {
    const res = await axiosClient.get<ListQuestionnairesResponse>(base, { params });
    return res.data.rows;
  },

  get: async (code: string): Promise<AdminQuestionnaireDetail> => {
    const res = await axiosClient.get<AdminQuestionnaireDetail>(
      `${base}/${encodeURIComponent(code)}`,
    );
    return res.data;
  },

  create: async (
    payload: CreateQuestionnairePayload,
  ): Promise<AdminQuestionnaireRow> => {
    const res = await axiosClient.post<AdminQuestionnaireRow>(base, payload);
    return res.data;
  },

  update: async (
    code: string,
    payload: UpdateQuestionnairePayload,
  ): Promise<AdminQuestionnaireRow> => {
    const res = await axiosClient.patch<AdminQuestionnaireRow>(
      `${base}/${encodeURIComponent(code)}`,
      payload,
    );
    return res.data;
  },

  publish: async (
    payload: PublishQuestionnairesRequest,
  ): Promise<PublishQuestionnairesResponse> => {
    const res = await axiosClient.post<PublishQuestionnairesResponse>(
      `${base}/publish`,
      payload,
    );
    return res.data;
  },

  publishOne: async (
    code: string,
    base_version: number,
    options?: { batch_id?: string; change_summary?: string },
  ): Promise<PublishQuestionnairesResponse> => {
    return adminQuestionnaireApi.publish({
      entities: [{ questionnaire_code: code, base_version }],
      batch_id: options?.batch_id,
      change_summary: options?.change_summary,
    });
  },

  rollback: async (
    code: string,
    payload: RollbackQuestionnairePayload,
  ): Promise<AdminQuestionnaireRow> => {
    const res = await axiosClient.post<AdminQuestionnaireRow>(
      `${base}/${encodeURIComponent(code)}/rollback`,
      payload,
    );
    return res.data;
  },

  /* ---------------- Sub-entity: groups ---------------- */

  createGroup: async (
    questionnaireCode: string,
    payload: CreateGroupPayload,
  ): Promise<QuestionGroupRow> => {
    const res = await axiosClient.post<QuestionGroupRow>(
      `${base}/${encodeURIComponent(questionnaireCode)}/groups`,
      payload,
    );
    return res.data;
  },

  updateGroup: async (
    questionnaireCode: string,
    groupId: number,
    payload: UpdateGroupPayload,
  ): Promise<QuestionGroupRow> => {
    const res = await axiosClient.patch<QuestionGroupRow>(
      `${base}/${encodeURIComponent(questionnaireCode)}/groups/${groupId}`,
      payload,
    );
    return res.data;
  },

  deleteGroup: async (
    questionnaireCode: string,
    groupId: number,
  ): Promise<void> => {
    await axiosClient.delete(
      `${base}/${encodeURIComponent(questionnaireCode)}/groups/${groupId}`,
    );
  },

  /* ---------------- Sub-entity: questions ---------------- */

  createQuestion: async (
    questionnaireCode: string,
    groupId: number,
    payload: CreateQuestionPayload,
  ): Promise<QuestionRow> => {
    const res = await axiosClient.post<QuestionRow>(
      `${base}/${encodeURIComponent(questionnaireCode)}/groups/${groupId}/questions`,
      payload,
    );
    return res.data;
  },

  updateQuestion: async (
    questionnaireCode: string,
    groupId: number,
    questionId: number,
    payload: UpdateQuestionPayload,
  ): Promise<QuestionRow> => {
    const res = await axiosClient.patch<QuestionRow>(
      `${base}/${encodeURIComponent(questionnaireCode)}/groups/${groupId}/questions/${questionId}`,
      payload,
    );
    return res.data;
  },

  deleteQuestion: async (
    questionnaireCode: string,
    groupId: number,
    questionId: number,
  ): Promise<void> => {
    await axiosClient.delete(
      `${base}/${encodeURIComponent(questionnaireCode)}/groups/${groupId}/questions/${questionId}`,
    );
  },
};
